import { IntakeRole } from "../types/interviewSession";
import { buildWhyYouMatch } from "./whyYouMatchCopy";

export type MatchScoringInput = {
  sentimentScore: number;
  sentimentMagnitude: number;
  faceJoy: number;
  faceSorrow: number;
  faceAnger: number;
  transcripts: string[];
  journeyType: string;
  role: IntakeRole;
  allAnswerMagnitudes: number[];
  rubricAccountability?: number;
  rubricCoachability?: number;
  rubricFlags?: string[];
};

export type ScoringMentor = {
  id: string;
  name: string;
  title: string;
  skills: string[];
  industry: string;
  bio: string;
};

export type MatchBadge = "best_match" | "soul_match";

export type ScoredMatch = {
  mentor: ScoringMentor;
  compatibilityScore: number;
  badge: MatchBadge | null;
  whyYouMatch: string;
};

type MatchReasons = {
  sentimentHigh: boolean;
  journeyMatch: boolean;
  energyStyle: "high" | "calm" | null;
  matchedSkills: string[];
};

const COACHING_KEYWORDS = [
  "coaching",
  "leadership",
  "mentor",
  "coach",
  "leader",
  "executive",
];

const JOURNEY_KEYWORDS: Record<string, string[]> = {
  entrepreneur: ["founder", "startup", "venture", "entrepreneur"],
  corporate: ["director", "vp", "manager", "enterprise"],
  student: ["professor", "teacher", "coach"],
};

const HIGH_ENERGY_KEYWORDS = ["dynamic", "passionate", "energetic", "bold"];
const CALM_KEYWORDS = ["patient", "methodical", "structured", "steady"];

function mentorText(mentor: ScoringMentor): string {
  return `${mentor.title} ${mentor.bio} ${mentor.industry}`.toLowerCase();
}

function hasKeyword(text: string, keywords: string[]): boolean {
  return keywords.some((keyword) => text.includes(keyword));
}

function scoreSentiment(
  session: MatchScoringInput,
  mentor: ScoringMentor
): { score: number; sentimentHigh: boolean } {
  let score = 55;
  const text = mentorText(mentor);

  const rubricCoach =
    session.rubricCoachability ?? 55;
  const rubricAccount =
    session.rubricAccountability ?? 55;
  const blendedTone =
    session.sentimentScore > 0
      ? session.sentimentScore * 0.5 + (rubricCoach / 100) * 0.5
      : session.sentimentScore * 0.4 + (rubricAccount / 100) * 0.6;

  const sentimentHigh = blendedTone > 0.35;

  if (sentimentHigh && hasKeyword(text, COACHING_KEYWORDS)) {
    score = 92;
  } else if (sentimentHigh) {
    score = 72;
  } else if (blendedTone >= 0) {
    score = 60;
  } else {
    score = 45;
  }

  if (session.rubricFlags?.includes("low_coachability_signal")) {
    score = Math.min(score, 50);
  }

  return { score, sentimentHigh };
}

function scoreJourney(
  session: MatchScoringInput,
  mentor: ScoringMentor
): { score: number; journeyMatch: boolean } {
  const text = mentorText(mentor);
  const keywords = JOURNEY_KEYWORDS[session.journeyType] ?? [];
  const journeyMatch = hasKeyword(text, keywords);
  return { score: journeyMatch ? 95 : 50, journeyMatch };
}

function scoreEnergy(
  session: MatchScoringInput,
  mentor: ScoringMentor
): { score: number; energyStyle: "high" | "calm" | null } {
  const text = mentor.bio.toLowerCase();
  let energyStyle: "high" | "calm" | null = null;
  let score = 55;

  if (session.faceJoy > 0.6) {
    energyStyle = "high";
    score = hasKeyword(text, HIGH_ENERGY_KEYWORDS) ? 90 : 68;
  } else if (session.faceJoy < 0.3) {
    energyStyle = "calm";
    score = hasKeyword(text, CALM_KEYWORDS) ? 90 : 68;
  }

  return { score, energyStyle };
}

function scoreSkills(
  session: MatchScoringInput,
  mentor: ScoringMentor
): { score: number; matchedSkills: string[] } {
  const transcriptText = session.transcripts.join(" ").toLowerCase();
  const matchedSkills = mentor.skills.filter((skill) => {
    const normalized = skill.toLowerCase().trim();
    if (!normalized) return false;
    return (
      transcriptText.includes(normalized) ||
      normalized.split(/\s+/).some((part) => part.length > 3 && transcriptText.includes(part))
    );
  });

  if (mentor.skills.length === 0) {
    return { score: 40, matchedSkills: [] };
  }

  const ratio = matchedSkills.length / mentor.skills.length;
  return { score: Math.round(35 + ratio * 65), matchedSkills };
}

function clampScore(value: number): number {
  return Math.max(0, Math.min(100, Math.round(value)));
}

function isSoulMatch(session: MatchScoringInput): boolean {
  if (session.allAnswerMagnitudes.length === 0) return false;
  return session.allAnswerMagnitudes.every((magnitude) => magnitude > 0.8);
}

export function scoreMentors(
  session: MatchScoringInput,
  mentors: ScoringMentor[],
  translate?: (key: string, options?: Record<string, unknown>) => string
): ScoredMatch[] {
  const scored = mentors.map((mentor) => {
    const sentiment = scoreSentiment(session, mentor);
    const journey = scoreJourney(session, mentor);
    const energy = scoreEnergy(session, mentor);
    const skills = scoreSkills(session, mentor);

    let compatibilityScore =
      sentiment.score * 0.4 +
      journey.score * 0.3 +
      energy.score * 0.2 +
      skills.score * 0.1;

    if (session.sentimentScore < 0) {
      compatibilityScore *= 0.8;
    }

    if (session.rubricFlags?.includes("external_blame_signal")) {
      compatibilityScore *= 0.9;
    }

    if (session.rubricCoachability != null && session.rubricCoachability < 40) {
      compatibilityScore *= 0.92;
    }

    const reasons: MatchReasons = {
      sentimentHigh: sentiment.sentimentHigh,
      journeyMatch: journey.journeyMatch,
      energyStyle: energy.energyStyle,
      matchedSkills: skills.matchedSkills,
    };

    return {
      mentor,
      compatibilityScore: clampScore(compatibilityScore),
      badge: null as MatchBadge | null,
      whyYouMatch: buildWhyYouMatch(session, mentor, reasons, translate),
    };
  });

  scored.sort((a, b) => b.compatibilityScore - a.compatibilityScore);

  if (scored.length > 0 && session.sentimentMagnitude > 0.5) {
    scored[0].compatibilityScore = clampScore(scored[0].compatibilityScore * 1.1);
  }

  scored.sort((a, b) => b.compatibilityScore - a.compatibilityScore);

  const soulMatch = isSoulMatch(session);

  scored.forEach((match, index) => {
    if (index === 0) {
      match.badge = soulMatch ? "soul_match" : "best_match";
    }
  });

  return scored;
}
